import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import EntryForm from './components/EntryForm';
import SystemsGuide from './components/SystemsGuide';
import { motion } from 'framer-motion';

function App() {
  const [entries, setEntries] = useState([]);

  // Load entries from localStorage on component mount
  useEffect(() => {
    const savedEntries = localStorage.getItem('foundationSprintEntries');
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }
  }, []);

  // Save entries to localStorage whenever entries change
  useEffect(() => {
    localStorage.setItem('foundationSprintEntries', JSON.stringify(entries));
  }, [entries]);

  const calculateMetrics = (entry) => {
    // Only calculate if "After" data exists
    const hasResults = entry.timeAfter !== null && entry.timeAfter !== undefined && entry.timeAfter !== '';
    return {
      timeSaved: hasResults ? (entry.timeBefore - entry.timeAfter) : 0,
      stepsReduced: hasResults ? (entry.stepsBefore - entry.stepsAfter) : 0,
      confusionReduction: hasResults ? (entry.confusionBefore - entry.confusionAfter) : 0,
      status: hasResults ? 'has-results' : 'in-progress'
    };
  };

  const addEntry = (entryData) => {
    const metrics = calculateMetrics(entryData);
    const newEntry = {
      ...entryData,
      ...metrics,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      is_complete: false, // Add completion field
      log_count: 0 // Add log count field
    };
    setEntries(prev => [newEntry, ...prev]);
  };

  const updateEntry = (id, updatedData) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === id) {
        // Check if this is a log results operation (has after data)
        const isLoggingResults = updatedData.timeAfter !== null && updatedData.timeAfter !== undefined && updatedData.timeAfter !== '';
        
        let newEntry = { ...entry, ...updatedData };
        
        // Only update log_count if this is a new results log
        if (isLoggingResults) {
          // Check log limit
          if (entry.log_count >= 5) {
            // Don't allow logging beyond limit
            return entry;
          }
          newEntry.log_count = (entry.log_count || 0) + 1;
        }
        
        // Calculate metrics based on latest results
        const metrics = calculateMetrics(newEntry);
        newEntry = { ...newEntry, ...metrics };
        
        return newEntry;
      }
      return entry;
    }));
  };

  const updateCompletionStatus = (id, isComplete) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === id) {
        return { ...entry, is_complete: isComplete };
      }
      return entry;
    }));
  };

  const deleteEntry = (id) => {
    setEntries(prev => prev.filter(entry => entry.id !== id));
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <motion.main 
          className="container mx-auto px-4 py-8 max-w-7xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Routes>
            <Route 
              path="/" 
              element={<Dashboard 
                entries={entries} 
                onUpdateEntry={updateEntry} 
                onUpdateCompletion={updateCompletionStatus}
                onDeleteEntry={deleteEntry} 
              />} 
            />
            <Route 
              path="/new-entry" 
              element={<EntryForm onSubmit={addEntry} />} 
            />
            <Route 
              path="/edit/:id" 
              element={<EntryForm 
                entries={entries} 
                onSubmit={updateEntry} 
                isEdit={true} 
              />} 
            />
            <Route path="/systems-guide" element={<SystemsGuide />} />
          </Routes>
        </motion.main>
      </div>
    </Router>
  );
}

export default App;